import { useState } from "react";
import { AlertTriangle, Wrench } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Painel do dono/gerente: carros fundados sem emplacamento (RMV), cada um com
// responsável + prazo. Atrasados sobem pro topo em vermelho.
export default function RmvTaskList({ cars = [], team = [], onFix }) {
  const [patches, setPatches] = useState({});
  const today = new Date().toISOString().slice(0, 10);

  const get = (v, k, orig) => (patches[v.id]?.[k] !== undefined ? patches[v.id][k] : (v[orig] || ""));

  const saveTask = async (v, patch, persist = true) => {
    const next = {
      assignee_id: patch.assignee_id !== undefined ? patch.assignee_id : get(v, "assignee_id", "rmv_task_assignee_id"),
      assignee_name: patch.assignee_name !== undefined ? patch.assignee_name : get(v, "assignee_name", "rmv_task_assignee_name"),
      due: patch.due !== undefined ? patch.due : get(v, "due", "rmv_task_due"),
    };
    setPatches((p) => ({ ...p, [v.id]: next }));
    if (!persist) return;
    try {
      await api.put(`/vehicles/${v.id}/rmv-task`, next);
      toast.success("Tarefa de emplacamento salva");
    } catch {
      toast.error("Erro ao salvar a tarefa");
    }
  };

  const dueOf = (v) => get(v, "due", "rmv_task_due");
  const isOverdue = (v) => { const d = dueOf(v); return d && d < today; };

  const sorted = [...cars].sort((a, b) => {
    if (isOverdue(a) !== isOverdue(b)) return isOverdue(a) ? -1 : 1;
    const da = dueOf(a) || "9999", db = dueOf(b) || "9999";
    if (da !== db) return da < db ? -1 : 1;
    return (a.funded_at || "") < (b.funded_at || "") ? -1 : 1;
  });

  const overdueCount = cars.filter(isOverdue).length;

  return (
    <section data-testid="rmv-task-list" className="mt-10 border border-warning/40">
      <div className="bg-warning/[0.06] px-4 py-3 border-b border-warning/40 flex flex-wrap items-center justify-between gap-2">
        <p className="font-display font-black uppercase tracking-tight text-warning inline-flex items-center gap-2">
          <AlertTriangle size={15} /> Emplacamento pendente (RMV) · {cars.length}
        </p>
        <p className="text-[10px] uppercase tracking-widest text-text-secondary">
          Defina responsável e prazo pra cada carro
          {overdueCount > 0 && <span className="text-brand font-bold"> · {overdueCount} atrasado{overdueCount > 1 ? "s" : ""}</span>}
        </p>
      </div>
      <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
        {sorted.map((v) => (
          <div
            key={v.id}
            data-testid={`rmv-task-${v.id}`}
            className={`px-4 py-3 flex flex-wrap items-center gap-3 ${isOverdue(v) ? "bg-brand/[0.07] border-l-2 border-brand" : ""}`}
          >
            <div className="flex-1 min-w-[180px]">
              <p className="font-display font-bold text-sm">
                {v.store_badge && (
                  <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 mr-2 ${v.store_badge.clr}`}>{v.store_badge.label}</span>
                )}
                {v.year} {v.make} {v.model}
              </p>
              <p className="text-[10px] text-text-secondary mt-0.5">
                {v.buyer_name ? `Cliente: ${v.buyer_name}` : ""}
                {v.funded_at ? `${v.buyer_name ? " · " : ""}Fundado em ${String(v.funded_at).slice(0, 10)}` : ""}
                {isOverdue(v) && <span className="text-brand font-bold uppercase tracking-wider"> · Atrasado</span>}
              </p>
            </div>
            <select
              data-testid={`rmv-assignee-${v.id}`}
              value={get(v, "assignee_id", "rmv_task_assignee_id")}
              onChange={(e) => {
                const m = team.find((x) => x.id === e.target.value);
                saveTask(v, { assignee_id: e.target.value, assignee_name: m ? (m.full_name || m.name || "") : "" });
              }}
              className="bg-surface border border-border focus:border-primary focus:outline-none px-2 h-9 text-xs w-[150px]"
            >
              <option value="">— Responsável —</option>
              {team.map((m) => (
                <option key={m.id} value={m.id}>{m.full_name || m.name}</option>
              ))}
            </select>
            <input
              data-testid={`rmv-due-${v.id}`}
              type="date"
              value={dueOf(v)}
              onChange={(e) => saveTask(v, { due: e.target.value }, false)}
              onBlur={(e) => saveTask(v, { due: e.target.value })}
              className={`bg-surface border focus:border-primary focus:outline-none px-2 h-9 text-xs w-[140px] ${isOverdue(v) ? "border-brand text-brand" : "border-border"}`}
            />
            <button
              type="button"
              data-testid={`rmv-fix-${v.id}`}
              onClick={() => onFix && onFix(v)}
              className="border border-warning text-warning hover:bg-warning hover:text-black transition-colors px-3 h-9 text-[10px] font-display font-bold uppercase tracking-widest inline-flex items-center gap-1.5"
            >
              <Wrench size={12} /> Preencher RMV
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
